import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Dialog, IconButton } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { getAssetsBaseUrl } from '../../config/assetsUrl';

const hotelImages = Array.from({ length: 12 }, (_, i) => `${i + 1}.jpg`);

const HotelGallery: React.FC = () => {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const images = useMemo(
        () =>
            hotelImages.map((file) => ({
                name: file,
                src: `${getAssetsBaseUrl()}/images/hotel/${file}`,
            })),
        []
    );

    const handleClose = () => setActiveIndex(null);
    const handlePrev = () => {
        if (activeIndex === null) return;
        setActiveIndex((activeIndex - 1 + images.length) % images.length);
    };
    const handleNext = () => {
        if (activeIndex === null) return;
        setActiveIndex((activeIndex + 1) % images.length);
    };

    useEffect(() => {
        if (activeIndex === null) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') handlePrev();
            if (event.key === 'ArrowRight') handleNext();
            if (event.key === 'Escape') handleClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeIndex, images.length]);

    return (
        <>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" },
                    gap: { xs: 1.5, sm: 2 },
                }}
            >
                {images.map((image, index) => (
                    <Box
                        key={image.name}
                        onClick={() => setActiveIndex(index)}
                        sx={{
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: 2,
                            cursor: "pointer",
                            backgroundColor: "rgba(0,0,0,0.05)",
                            aspectRatio: "1 / 1",
                            transition: "transform 0.25s ease",
                            "&:hover img": {
                                transform: "scale(1.04)",
                            },
                        }}
                    >
                        <Box
                            component="img"
                            src={image.src}
                            alt={`Hotel ${index + 1}`}
                            loading="lazy"
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                                transition: "transform 0.25s ease",
                            }}
                        />
                    </Box>
                ))}
            </Box>

            <Dialog
                open={activeIndex !== null}
                onClose={handleClose}
                fullScreen
                PaperProps={{
                    sx: {
                        backgroundColor: 'rgba(0,0,0,0.95)',
                        boxShadow: 'none',
                    },
                }}
            >
                {activeIndex !== null && (
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            p: { xs: 2, sm: 3 },
                        }}
                    >
                        <IconButton
                            onClick={handleClose}
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.4)',
                            }}
                        >
                            <CloseIcon />
                        </IconButton>

                        <IconButton
                            onClick={handlePrev}
                            sx={{
                                position: 'absolute',
                                left: { xs: 8, md: 20 },
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.4)',
                            }}
                        >
                            <ChevronLeftIcon />
                        </IconButton>

                        <Box
                            component="img"
                            src={images[activeIndex].src}
                            alt={images[activeIndex].name}
                            sx={{
                                maxWidth: '96vw',
                                maxHeight: '92vh',
                                objectFit: 'contain',
                                borderRadius: 1,
                            }}
                        />

                        <IconButton
                            onClick={handleNext}
                            sx={{
                                position: 'absolute',
                                right: { xs: 8, md: 20 },
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.4)',
                            }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>
                )}
            </Dialog>
        </>
    );
};

export default HotelGallery;
